class FreeGiftManager {
  constructor(config, collectionCache) {
    this.config = config.firstOrderGift || null;
    this.customerHasOrders = config.customerHasOrders === true;
    this.collectionCache = collectionCache;
    this.freeGiftVariantId = this.config?.variantId || null;
    this.firstOrderGiftFreeSubscriptionOnly = this.config?.subscriptionOnly === true;
    this.eligibleCollectionHandle = this.config?.eligibleCollectionHandle || null;
    this.eligibleProductIds = null;
    this.isValidatingCart = false;
    this.cachedCart = null;
    this.previousCartItemCount = 0;
  }

  get enabled() {
    return !!this.freeGiftVariantId;
  }

  async resolveEligibility() {
    let ids = null;
    if (this.eligibleCollectionHandle) {
      ids = await this.collectionCache.getProductIds(this.eligibleCollectionHandle);
    }
    this.eligibleProductIds = this.collectionCache.mergeExtraIds(ids, this.config?.eligibleProductIds);
  }

  checkEligibility() {
    if (this.customerHasOrders) return false;
    return true;
  }

  updateCachedCart(cartData) {
    if (cartData?.items) {
      this.previousCartItemCount = this.cachedCart ? this.cachedCart.items.length : 0;
      this.cachedCart = cartData;
    }
  }

  hasFreeGiftInCart(cart) {
    if (!cart?.items || !this.freeGiftVariantId) return false;
    return cart.items.some(
      (item) => parseInt(item.variant_id, 10) === parseInt(this.freeGiftVariantId, 10)
    );
  }

  hasEligibleProductInCart(cart) {
    if (!cart?.items) return false;

    const nonFreeGiftItems = cart.items.filter(
      (item) => parseInt(item.variant_id, 10) !== parseInt(this.freeGiftVariantId, 10)
    );

    if (nonFreeGiftItems.length === 0) return false;

    if (!this.eligibleProductIds || this.eligibleProductIds.size === 0) {
      if (!this.firstOrderGiftFreeSubscriptionOnly) return true;
      return nonFreeGiftItems.some((item) => !!item.selling_plan_allocation);
    }

    return nonFreeGiftItems.some((item) => {
      const productId = parseInt(item.product_id, 10);
      if (isNaN(productId) || !this.eligibleProductIds.has(productId)) return false;
      if (!this.firstOrderGiftFreeSubscriptionOnly) return true;
      return !!item.selling_plan_allocation;
    });
  }

  checkAndResetFirstOrderStatus() {
    const isFirstOrder = localStorage.getItem('isFirstOrder') !== 'false';
    if (isFirstOrder) return false;

    const firstOrderOriginalTime = localStorage.getItem('firstOrderOriginalTime');
    if (!firstOrderOriginalTime) return false;

    const timeSinceFirstOrder = Date.now() - parseInt(firstOrderOriginalTime, 10);
    if (timeSinceFirstOrder <= 2 * 24 * 60 * 60 * 1000) {
      localStorage.setItem('isFirstOrder', 'true');
      localStorage.removeItem('ADD_FIRST_ORDER_GIFT_FREE');
      return true;
    }

    return false;
  }

  trackFreeGiftAbuse(attemptedQuantity, itemCount) {
    const timestamp = new Date().toISOString();
    const abuseCount = parseInt(localStorage.getItem('free_gift_abuse_count') || '0', 10) + 1;
    localStorage.setItem('free_gift_abuse_count', abuseCount.toString());
    localStorage.setItem('free_gift_abuse_last_attempt', timestamp);

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          _free_gift_abuse_attempted: 'true',
          _free_gift_abuse_timestamp: timestamp,
          _free_gift_abuse_attempted_quantity: attemptedQuantity.toString(),
          _free_gift_abuse_item_count: itemCount.toString(),
          _free_gift_abuse_count: abuseCount.toString(),
        },
      }),
    }).catch(() => {});
  }

  #getFreeGiftItems(cart) {
    return cart.items.filter(
      (item) => parseInt(item.variant_id, 10) === parseInt(this.freeGiftVariantId, 10)
    );
  }

  async addFreeGiftAfterReset() {
    if (!this.freeGiftVariantId || this.customerHasOrders) return null;

    const cart = await GiftCart.fetchCart();
    if (!cart) return null;

    const hasFreeGift = this.hasFreeGiftInCart(cart);
    const hasOtherProducts = cart.items.some(
      (item) => parseInt(item.variant_id, 10) !== parseInt(this.freeGiftVariantId, 10)
    );

    if (hasFreeGift || !hasOtherProducts) return null;
    if (GiftCart.hasKachingBundleFreeGiftInCart(cart, this.freeGiftVariantId)) return null;
    if (!this.hasEligibleProductInCart(cart)) return null;

    const updatedCart = await GiftCart.addToCart(this.freeGiftVariantId, 1);
    if (updatedCart) {
      this.updateCachedCart(updatedCart);
      localStorage.setItem('ADD_FIRST_ORDER_GIFT_FREE', Date.now().toString());
    }
    return updatedCart;
  }

  /**
   * Returns cart updates needed, or null if no change.
   * @returns {{ updates: Object, abuse?: boolean, removeStorage?: boolean } | null}
   */
  getSyncUpdates(cart, { pageLoad = false } = {}) {
    if (!this.enabled || !cart?.items) return null;

    const freeGiftItems = this.#getFreeGiftItems(cart);
    const hasOtherProducts = cart.items.some(
      (item) => parseInt(item.variant_id, 10) !== parseInt(this.freeGiftVariantId, 10)
    );
    const hasEligibleProduct = this.hasEligibleProductInCart(cart);
    const currentItemCount = cart.items.length;
    const itemCountIncreased = currentItemCount > this.previousCartItemCount;

    if (freeGiftItems.length === 0) {
      if (hasEligibleProduct && itemCountIncreased) {
        const wasReset = this.checkAndResetFirstOrderStatus();
        if (wasReset) return { addGift: true };
      }
      return null;
    }

    if (!hasEligibleProduct) {
      return { updates: { [this.freeGiftVariantId]: 0 }, removeStorage: true };
    }

    if (
      hasOtherProducts &&
      GiftCart.hasKachingBundleFreeGiftInCart(cart, this.freeGiftVariantId) &&
      freeGiftItems.some((item) => !GiftCart.itemHasKachingBundleProperty(item))
    ) {
      return { updates: { [this.freeGiftVariantId]: 0 }, removeStorage: true };
    }

    const totalQuantity = freeGiftItems.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQuantity > 1 || freeGiftItems.length > 1) {
      return { updates: { [this.freeGiftVariantId]: 1 }, abuse: true };
    }

    if (pageLoad) {
      if (freeGiftItems.length > 0 && !hasOtherProducts) {
        return { updates: { [this.freeGiftVariantId]: 0 }, removeStorage: true };
      }
      if (freeGiftItems.length > 0 && !this.checkEligibility()) {
        return { updates: { [this.freeGiftVariantId]: 0 } };
      }
    }

    return null;
  }

  async enforceCart(cart, options = {}) {
    if (!this.enabled || !cart) return { cart, changed: false };

    const syncResult = this.getSyncUpdates(cart, options);

    if (syncResult?.addGift) {
      const updatedCart = await this.addFreeGiftAfterReset();
      return { cart: updatedCart || cart, changed: !!updatedCart };
    }

    if (!syncResult?.updates) return { cart, changed: false };

    if (syncResult.abuse) {
      this.trackFreeGiftAbuse(
        GiftCart.getVariantQty(cart, this.freeGiftVariantId),
        this.#getFreeGiftItems(cart).length
      );
    }

    const updatedCart = await GiftCart.applyCartUpdates(syncResult.updates);
    if (!updatedCart) return { cart, changed: false };

    if (syncResult.removeStorage) {
      localStorage.removeItem('ADD_FIRST_ORDER_GIFT_FREE');
    }

    this.updateCachedCart(updatedCart);
    return { cart: updatedCart, changed: true };
  }

  async enforceFreeGiftRules(cart) {
    const result = await this.enforceCart(cart, { pageLoad: false });
    return result.cart;
  }
}

window.FreeGiftManager = FreeGiftManager;
